import React, {
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { css } from '@emotion/react';
import t from 'react-translate';

// Redux
import { useSelector, useDispatch } from 'react-redux';
import { getCurrentUser } from 'redux/selectors/users';
import { getAiRolePlay } from 'redux/selectors/timeline';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { startRolePlaySession, StartSessionPayload } from 'redux/actions/ai-role-play';
import { wrapThunkAction } from 'redux/utils';

import {
  ReactComponent as AIPersona,
} from 'styles/icons/ai-persona.svg';
import ProfilePicture from 'org_level_profile/components/profile-picture';
import { Button } from 'react-bootstrap';
import {
  doubleSpacing,
  quarterSpacing,
  shortBar,
  tripleSpacing,
} from 'styles/global_defaults/scaffolding';
import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import { danger, genAIGradientType4 } from 'styles/global_defaults/colors';
import AiConversationContext, { AIRolePlayStatus, MessageStatus, Sender } from './context';
import ConversationModalHeader from './header';
import ScenarioDetails from './scenario-details';
import ChatBar from './chat-bar';
import AiChat from './ai-chat';
import LearnerChat from './learner-chat';
import useAiRolePlayHelper from '../hooks/use-ai-role-play-helper';
import streamAIResponse from './stream-ai-response';

const ConversationModalContent = () => {
  const { getResponsivePadding } = useAiRolePlayHelper();

  const styles = css`
  border-top: ${quarterSpacing}px solid transparent;
  border-image-source: ${genAIGradientType4};
  border-image-slice: 1;
  .body-container {
    padding-left: ${getResponsivePadding()}px;
    padding-right: ${getResponsivePadding()}px;
    overflow-y: auto;
    .scenario-container {
      flex-grow: 1;
      .avatar-container {
        .profile {
          width: ${shortBar}px;
        }
      }
      .scenario-toggle-button {
        margin-top: 25px;
      }
    }
  }
  .scroll-handler {
    width: ${doubleSpacing}px;
    min-height: ${doubleSpacing}px;
  }
`;

  const {
    aiRolePlayId,
    aiRolePlayStatus,
    setAiRolePlayStatus,
    showScenarioDetails,
    setShowScenarioDetails,
    messages,
    setMessages,
    setSessionData,
  } = useContext(AiConversationContext);
  const currentUser = useSelector(getCurrentUser);
  const currentCatalogId = useSelector((state) => state.app.currentCatalogId);
  const aiRolePlay = useSelector((state) => getAiRolePlay(state, aiRolePlayId));
  const dispatch = useDispatch();
  const bodyRef = useRef(null);

  const [isBodyScrollPresent, setIsBodyScrollPresent] = useState(false);
  const [isBodyScrollBarAtBottom, setIsBodyScrollBarAtBottom] = useState(true);

  useEffect(() => {
    const bodyElement = bodyRef.current;
    let resizeObserver: ResizeObserver | null = null;
    const checkScroll = () => {
      setIsBodyScrollPresent(bodyElement.scrollHeight > bodyElement.clientHeight);
      const isAtBottom = Math.abs(bodyElement.scrollHeight - bodyElement.scrollTop - bodyElement.clientHeight) < 1;
      setIsBodyScrollBarAtBottom(isAtBottom);
    };

    if (bodyElement) {
      bodyElement.addEventListener('scroll', checkScroll);
      // ResizeObserver to detect content/height changes and keep scroll state in sync.
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => {
          checkScroll();
        });
        resizeObserver.observe(bodyElement);
      }
    }

    return () => {
      if (bodyElement) {
        bodyElement.removeEventListener('scroll', checkScroll);
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      }
    };
  }, []);

  const {
    aiCharacterName,
    aiCharacterRole,
    learnerRole,
  } = aiRolePlay || {};

  const handleToggleScenarioDetails = () => {
    setShowScenarioDetails(!showScenarioDetails);
  };

  // To pass as callback to streamAIResponse for handling incoming response chunks and updating the conversation in real-time.
  const handleChunk = (chunk) => {
    setMessages((prevMessages) => {
      const lastMessage = prevMessages[prevMessages.length - 1];
      if (lastMessage && lastMessage.sender === Sender.AI && lastMessage.status === MessageStatus.PENDING) {
        // Update the pending AI message with new content.
        const updatedMessage = {
          ...lastMessage,
          content: lastMessage.content + chunk.content,
          status: chunk.is_last_chunk ? MessageStatus.SENT : MessageStatus.PENDING,
        };
        return [...prevMessages.slice(0, -1), updatedMessage];
      }
      // If no pending AI message, add a new one.
      return [...prevMessages,
        { content: chunk.content,
          sender: Sender.AI,
          status: chunk.is_last_chunk ? MessageStatus.SENT : MessageStatus.PENDING,
        }];
    });
  };

  const handleError = () => {
    dispatch(addAlertMessage({
      type: AlertMessageType.WARNING,
      header: t.FORM.OOPS(),
      message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SOMETHING_WENT_WRONG(),
      customColor: danger,
    }));
    setMessages((prevMessages) => {
      const lastMessage = prevMessages[prevMessages.length - 1];
      if (lastMessage && lastMessage.sender === Sender.AI && lastMessage.status === MessageStatus.PENDING) {
        const updatedMessage = {
          ...lastMessage,
          status: MessageStatus.FAILED,
        };
        return [...prevMessages.slice(0, -1), updatedMessage];
      }
      return prevMessages;
    });
  };

  const handleStartConversation = () => {
    wrapThunkAction(
      dispatch(startRolePlaySession({
        catalogId: currentCatalogId,
        aiRolePlayId,
      })),
    ).then((res) => {
      const sessionPayload = res.payload as StartSessionPayload;

      if (!sessionPayload?.jwtToken) {
        handleError();
        return;
      }

      setShowScenarioDetails(false);
      setAiRolePlayStatus(AIRolePlayStatus.IN_PROGRESS);
      setSessionData({
        jwtToken: sessionPayload.jwtToken,
        submissionId: sessionPayload.submissionId || null,
        expiresAt: sessionPayload.expiresAt || null,
      });

      streamAIResponse({
        token: sessionPayload.jwtToken,
        userMessage: null,
        onChunk: handleChunk,
        onError: handleError,
      });
    }).catch(() => {
      handleError();
    });
  };

  const handleScroll = () => {
    if (bodyRef.current) {
      if (!isBodyScrollBarAtBottom) {
        bodyRef.current.scrollTo({
          top: bodyRef.current.scrollHeight,
          behavior: 'smooth',
        });
        return;
      }
      bodyRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div css={styles} className='modal-page position-relative d-flex flex-column'>
      <ConversationModalHeader />
      <div ref={bodyRef} className='body-container border-top d-flex flex-column'>
        <div className='mt-5 scenario-container'>
          <div className='avatar-container d-flex justify-content-center'>
            <div className='profile d-flex flex-column justify-content-center align-items-center mt-2'>
              <AIPersona
                width={tripleSpacing}
                height={tripleSpacing}
                role='img'
                aria-label={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.AI_CHARACTER_AVATAR()}
              />
              <p className='mb-0 mt-2 text-regular semi-bold'>{aiCharacterName}</p>
              <p className='mb-0 text-small text-gray-2'>{aiCharacterRole}</p>
            </div>
            <div className='profile d-flex flex-column justify-content-center align-items-center mt-2'>
              <ProfilePicture
                profile={currentUser}
                isRound
                size={tripleSpacing}
              />
              <p className='mb-0 mt-2 text-regular semi-bold'>{t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.YOU()}</p>
              <p className='mb-0 text-small text-gray-2'>{learnerRole}</p>
            </div>
          </div>
          {showScenarioDetails && (
            <ScenarioDetails />
          )}
          {(aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS || aiRolePlayStatus === AIRolePlayStatus.COMPLETED)
            && (
            <ClickableContainer
              className='mb-0 text-primary d-flex justify-content-center scenario-toggle-button text-small semi-bold'
              onClick={handleToggleScenarioDetails}
            >
              <NvIcon
                icon='gen-ai'
                size='xss-smallest'
                className='d-flex align-items-center'
              />
              <p className='mb-0 ml-1'>
                {showScenarioDetails
                  ? t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.HIDE_SCENARIO()
                  : t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SHOW_SCENARIO()}
              </p>
            </ClickableContainer>
            )}
          {aiRolePlayStatus === AIRolePlayStatus.NOT_STARTED && (
            <div className='d-flex justify-content-center mt-5'>
              <Button
                onClick={handleStartConversation}
              >
                {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.START_CONVERSATION()}
              </Button>
            </div>
          )}
          {(aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS || aiRolePlayStatus === AIRolePlayStatus.COMPLETED)
            && (
            <div className='d-flex flex-column'>
              {
                messages.map((message) => {
                  if (message.sender === Sender.AI) {
                    return (
                      <div className='w-100 mt-5'>
                        <AiChat content={message.content} />
                      </div>
                    );
                  }
                  return (
                    <div className='w-100 mt-5 d-flex'>
                      <LearnerChat content={message.content} />
                    </div>
                  );
                })
              }
            </div>
            )}
        </div>
      </div>
      {isBodyScrollPresent && (
        <ClickableContainer
          className='d-flex bg-gray-2 scroll-handler rounded-circle mt-auto ml-auto mr-6'
          onClick={handleScroll}
        >
          <NvIcon
            className='text-white d-flex align-items-center m-auto'
            icon={isBodyScrollBarAtBottom ? 'sorting-up-active' : 'sorting-down-active'}
            size='xss-smallest'
          />
        </ClickableContainer>
      )}
      {aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS && (
        <ChatBar handleChunk={handleChunk} handleError={handleError} />
      )}
    </div>
  );
};

export default ConversationModalContent;
